import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { BellIcon, ChevronDownIcon, IdCardIcon, MapPinIcon, MenuIcon, SearchIcon, SmartphoneIcon, UserRoundIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { LanguageToggle } from './LanguageToggle';
import { BellPreview } from './avis/BellPreview';
import { AlertesDialog } from './avis/AlertesDialog';
import { HeaderTooltip } from './HeaderTooltip';
import { useAuth } from '../contexts/AuthContext';
import { navItems } from '../data/navigation';

/** Raccourcis bciti+ repris dans le tiroir mobile. */
const bcitiActions = [{
  label: 'Plan',
  to: '/plan',
  icon: MapPinIcon
}, {
  label: 'Ma carte',
  to: '/ma-carte',
  icon: IdCardIcon
}, {
  label: 'Mon espace',
  to: '/mon-espace',
  icon: SmartphoneIcon
}, {
  label: 'Mon profil',
  to: '/mon-profil',
  icon: UserRoundIcon
}];
const iconButton = 'group relative grid h-11 w-11 place-items-center rounded-full text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-mist hover:text-ville-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue';

/**
 * En-tête de Beaconsfield, sur une seule rangée.
 *
 * La bande utilitaire vert pâle du site réel a sauté : elle n'apportait qu'un filet de
 * liens de 12 px au-dessus du vrai en-tête. Ce qu'elle portait est soit remonté ici en
 * icône de plein format (Plan, FR/EN), soit retiré (sélecteur de taille de texte,
 * « Nous joindre »).
 *
 * Reste une seule ligne : le logo, les trois menus, puis à droite Plan et Ma carte
 * côte à côte, les trois services personnels, la bascule de langue et la loupe.
 */
export function Header() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [alertesOpen, setAlertesOpen] = useState(false);
  const {
    isAuthenticated
  } = useAuth();
  return <header className="relative w-full border-b border-ville-mist bg-white" data-id="element-1647" data-mp="1rdti6deuavct">
      <div className="mx-auto flex max-w-[1400px] items-center gap-6 px-5 py-4 lg:px-10" onMouseLeave={() => setOpenMenu(null)} data-id="element-1648" data-mp="1rfxikldj5c8c">
        <Link to="/" className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue" data-id="element-1649" data-mp="1hmqceyayts77">
          <Logo data-id="element-1650" data-mp="1mla1h7kuvk3s" />
        </Link>

        <nav aria-label="Navigation principale" className="hidden lg:block" data-id="element-1651" data-mp="1mki7202zvsf3">
          <ul className="flex items-center gap-2" data-id="element-1652" data-mp="18ls9kjc9jqq7">
            {navItems.map(item => {
            const isOpen = openMenu === item.label;
            return <li key={item.label} data-id="element-1653" data-mp="ypxlh8p9t3g1">
                  <button type="button" aria-expanded={isOpen} onMouseEnter={() => setOpenMenu(item.label)} onClick={() => setOpenMenu(isOpen ? null : item.label)} className={`flex items-center gap-2 rounded-full px-4 py-2 text-[15px] font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue ${isOpen ? 'text-ville-blue' : 'text-ville-navy hover:text-ville-blue'}`} data-id="element-1654" data-mp="yr0ebm65lfyl">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ville-lime" aria-hidden="true" data-id="element-1655" data-mp="w6kdr81zok3d" />
                    {item.label}
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ease-ville ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1656" data-mp="154mle6g8aazm" />
                  </button>
                </li>;
          })}
          </ul>
        </nav>

        <div className="ml-auto flex items-center gap-1.5" data-id="element-1657" data-mp="1a5k54ngvzd38">
          <Link to="/plan" aria-label="Plan de la ville" className={`hidden md:grid ${iconButton}`} data-id="element-1658" data-mp="3guf7o6rf8drz">
            <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-1659" data-mp="3bvvlc58x0zz3" />
            <HeaderTooltip label="Plan" data-id="element-1660" data-mp="3bxotxq339u9q" />
          </Link>

          <Link to="/ma-carte" aria-label="Ma carte" className={`hidden md:grid ${iconButton}`} data-id="element-1661" data-mp="22xm06oqvasiu">
            <IdCardIcon className="h-5 w-5" aria-hidden="true" data-id="element-1662" data-mp="27wp5maarf0rc" />
            <HeaderTooltip label="Ma carte" data-id="element-1663" data-mp="2cwv0dzcbt3dn" />
          </Link>

          <div className="relative hidden md:block" data-id="element-1664" data-mp="2cxmm8ig4fmqq">
            <button type="button" aria-label={isAuthenticated ? 'Mes notifications, 3 non lus dont une alerte urgente' : 'S’inscrire aux alertes citoyennes'} aria-expanded={isAuthenticated ? bellOpen : alertesOpen} onClick={() => isAuthenticated ? setBellOpen(v => !v) : setAlertesOpen(true)} className={iconButton} data-id="element-1665" data-mp="2gyyeywuako35">
              <BellIcon className="h-5 w-5" aria-hidden="true" data-id="element-1666" data-mp="11ho7pz26c8i4" />
              <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-ville-coral px-1 text-[11px] font-bold text-white ring-2 ring-white" aria-hidden="true" data-id="element-1667" data-mp="11h6cn5r900bs">
                {isAuthenticated ? '3' : '!'}
              </span>
              {!bellOpen && <HeaderTooltip label={isAuthenticated ? 'Mes notifications' : 'Alertes citoyennes'} data-id="element-1668" data-mp="oyu2435vufk1" />}
            </button>
            <AnimatePresence data-id="element-1669" data-mp="jz7lrkurcj70">
              {isAuthenticated && bellOpen && <BellPreview onNavigate={() => setBellOpen(false)} data-id="element-1670" data-mp="f1fu8ry0yz3c" />}
            </AnimatePresence>
          </div>

          <Link to="/mon-espace" aria-label="Mon espace" className={`hidden md:grid ${iconButton}`} data-id="element-1671" data-mp="a9xrbycfyofq">
            <SmartphoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-1672" data-mp="5abcxlkvn25c" />
            <HeaderTooltip label="Mon espace" data-id="element-1673" data-mp="19hp0s7fpncg" />
          </Link>

          <Link to="/mon-profil" aria-label="Mon profil" className={`hidden md:grid ${iconButton}`} data-id="element-1674" data-mp="1bcd2fe05vkr">
            <UserRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-1675" data-mp="3sf10bu5j3klf" />
            <HeaderTooltip label="Mon profil" data-id="element-1676" data-mp="3nhr51xvgkepo" />
          </Link>

          <div className="ml-1 hidden md:block" data-id="element-1677" data-mp="3dtdhccb2am45">
            <LanguageToggle data-id="element-1678" data-mp="3driv8uo20nyh" />
          </div>

          <button type="button" aria-label="Rechercher sur le site" className="ml-1 grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-mist text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-leaf hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-blue" data-id="element-1679" data-mp="3irx42exhc1xk">
            <SearchIcon className="h-5 w-5" aria-hidden="true" data-id="element-1680" data-mp="gae4pkq7d2y" />
          </button>

          <button type="button" aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} className={`lg:hidden ${iconButton}`} data-id="element-1681" data-mp="5cr2nkvu7tgk">
            {mobileOpen ? <XIcon className="h-5 w-5" data-id="element-1682" data-mp="jc9x5avq76ae" /> : <MenuIcon className="h-5 w-5" data-id="element-1683" data-mp="jayvbgluc8lx" />}
          </button>
        </div>
      </div>

      {/* Méga-menu : leurs trois sections en colonnes, comme sur le site réel. */}
      {openMenu && <div className="absolute inset-x-0 top-full z-50 hidden border-t border-ville-mist bg-white shadow-xl shadow-ville-navy/10 lg:block" onMouseEnter={() => setOpenMenu(openMenu)} onMouseLeave={() => setOpenMenu(null)} data-id="element-1684" data-mp="28s44av3q0nh8">
          <div className="mx-auto grid max-w-[1400px] gap-10 px-5 py-8 md:grid-cols-3 lg:px-10" data-id="element-1685" data-mp="2dlym1szrmsdo">
            {navItems.find(item => item.label === openMenu)?.groups.map(group => <nav key={group.title} aria-label={group.title} data-id="element-1686" data-mp="1kxt0j40w0zcs">
                  <h2 className="font-display text-sm font-bold uppercase tracking-wider text-ville-blue" data-id="element-1687" data-mp="1yw9hdnsgs68n">
                    {group.title}
                  </h2>
                  <ul className="mt-3 space-y-1.5" data-id="element-1688" data-mp="1uws80j9x2oy7">
                    {group.links.map(link => <li key={link} data-id="element-1689" data-mp="369l1byicvlgd">
                        <a href="#accueil" className="block text-sm text-slate-600 transition-colors duration-150 ease-ville hover:text-ville-navy" data-id="element-1690" data-mp="368jd0scbx2gp">
                          {link}
                        </a>
                      </li>)}
                  </ul>
                </nav>)}
          </div>
        </div>}

      {/* Tiroir mobile : les services bciti+ d'abord, les menus du site ensuite. */}
      {mobileOpen && <nav aria-label="Navigation mobile" className="border-t border-ville-mist bg-white px-5 pb-6 lg:hidden" data-id="element-1691" data-mp="1mfhxhzqalk9h">
          <p className="pt-4 font-display text-sm font-bold uppercase tracking-wider text-ville-blue" data-id="element-1692" data-mp="1reb89ke72x5y">
            Mon bciti+
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-1693" data-mp="21axsc6yp5k6x">
            {bcitiActions.map(action => <li key={action.label} data-id="element-1694" data-mp="1ylmdj48l0sb">
                <Link to={action.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-2xl bg-ville-mist px-4 py-3 text-[15px] font-bold text-ville-navy" data-id="element-1695" data-mp="1zn1byodi7fb">
                  <action.icon className="h-5 w-5" aria-hidden="true" data-id="element-1696" data-mp="2ff57jsfrqpqv" />
                  {action.label}
                </Link>
              </li>)}
          </ul>

          <div className="mt-5 border-t border-ville-mist pt-4" data-id="element-1697" data-mp="26frtemgp9czt">
            <LanguageToggle data-id="element-1698" data-mp="26df6rvzzyegf" />
          </div>

          <ul className="mt-2 divide-y divide-ville-mist" data-id="element-1699" data-mp="33628ka6102nc">
            {navItems.map(item => <li key={item.label} className="py-3" data-id="element-1700" data-mp="2z3xgvgz51g3c">
                <p className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-ville-navy" data-id="element-1701" data-mp="2z22yoaf9422y">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ville-lime" aria-hidden="true" data-id="element-1702" data-mp="2p69jske27wi0" />
                  {item.label}
                </p>
                {item.groups.map(group => <div key={group.title} className="mt-2" data-id="element-1703" data-mp="1dqb808rhywd3">
                    <p className="text-xs font-bold uppercase tracking-wider text-ville-blue" data-id="element-1704" data-mp="1dsxdm2utv1so">{group.title}</p>
                    <ul className="mt-1 space-y-1" data-id="element-1705" data-mp="1d0kyd3gx61yu">
                      {group.links.map(link => <li key={link} data-id="element-1706" data-mp="1msqpyc39ehdi">
                          <a href="#accueil" className="block py-1 text-[15px] text-slate-600" data-id="element-1707" data-mp="1mtsdhg1gowc4">
                            {link}
                          </a>
                        </li>)}
                    </ul>
                  </div>)}
              </li>)}
          </ul>
        </nav>}

      <AnimatePresence data-id="element-1708" data-mp="1qwf2782u6153">
        {alertesOpen && <AlertesDialog key="header-alertes" onClose={() => setAlertesOpen(false)} data-id="element-1709" data-mp="25ruxthj2e53l" />}
      </AnimatePresence>
    </header>;
}